import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSetRecoilState, useRecoilValue } from "recoil";
import { getPartnerMemberResponse } from "./api/partner/type";
import { PartnerLoginListResponse } from "./api/partnerLogin/partnerlist-type";
import { ApiResponse } from "./api/public-types";
import { PagePath } from "./pages/product/details";
import { partnerMemberState } from "./recoil/partnerMember/atom";
import { globalPartnerState, partnersState } from "./recoil/partners/atom";
import { useRedirectUrl } from "./recoil/redirect/hooks";
import { axiosInstance, maskEmail } from "./utils";
import * as Sentry from "@sentry/react";

/*
  PartnerProviderState는 partner 관련된 정보를 전역으로 관리하는 컴포넌트입니다.
  AuthProviderState와 함께 사용됩니다.
  AuthProviderState가 로그인이 되었을 때만 동작합니다.
  로그인이 되었을 때, partner 정보를 가져옵니다.
  recoil을 사용하여 전역으로 partner 정보를 관리합니다.
*/
function PartnerProviderState({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  const { isAuthenticated, getAccessTokenSilently, getIdTokenClaims, logout, user } = useAuth0();
  const setGlobalPartnerList = useSetRecoilState(partnersState);
  const setGlobalPartnerMember = useSetRecoilState(partnerMemberState);
  const selectedPartner = useRecoilValue(globalPartnerState);
  const { setRedirectUrl } = useRedirectUrl();
  const getPartners = async () => {
    await getAccessTokenSilently();
    const response = await getIdTokenClaims();
    const idToken = response?.__raw;

    return axiosInstance.get<ApiResponse<PartnerLoginListResponse>>("/api/ctrl/pa/partners", {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  };

  const getPartnerMember = async () => {
    await getAccessTokenSilently();
    const response = await getIdTokenClaims();
    const idToken = response?.__raw;

    return axiosInstance.get<ApiResponse<getPartnerMemberResponse>>("/api/ctrl/pa/me", {
      headers: { Authorization: `Bearer ${idToken}` },
    });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const result = await getPartners();
        const partners = result.data.data.activePartners;
        setGlobalPartnerList(partners);

        const partnerMember = await getPartnerMember();
        setGlobalPartnerMember(partnerMember.data.data.member);

        // 인증 시 Sentry에 마스킹된 유저 정보 세팅
        Sentry.setUser({
          id: user?.sub,
          email: maskEmail(user?.email || "", 2), // 앞 2글자만 표시
        });
      } catch (error: any) {
        console.log(error);
        // 로그인 페이지에서는 리다이렉트 URL 설정하지 않음
        if (location.pathname + location.search !== PagePath.login) {
          setRedirectUrl(location.pathname + location.search);
        }
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });

        // 미인증 및 로그아웃 시 Sentry 유저 정보 제거
        Sentry.setUser(null);
      }
    })();
  }, [isAuthenticated, user]);

  // 이벤트를 파트너 단위로 나눠 보기 위한 태그. 선택이 풀리면 undefined 로 덮어 지운다
  useEffect(() => {
    Sentry.setTag("ctrl.partner_id", selectedPartner?.id ? String(selectedPartner.id) : undefined);
  }, [selectedPartner?.id]);

  return <>{children}</>;
}
export default PartnerProviderState;
